import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Clock, Loader2 } from "lucide-react";

/**
 * ProposeExtraHoursDialog — Move-in Pro logs extra hours worked beyond the
 * original estimate. Their proposal pre-fills the student's "Mark done" dialog
 * for one-tap approval.
 */
export function ProposeExtraHoursDialog({ booking, onProposed }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(String(booking.proposed_extra_hours ?? "0"));
  const [submitting, setSubmitting] = useState(false);
  const value = Math.max(0, Math.min(12, Number(hours) || 0));

  const save = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/propose-extra-hours`, {
        extra_hours: value,
      });
      toast.success(value > 0
        ? `Logged ${value}h. The student will see this on their "Mark done" screen.`
        : "Cleared your extra hours."
      );
      setOpen(false);
      onProposed?.(booking.booking_id, data.proposed_extra_hours);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save extra hours");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`propose-hours-btn-${booking.booking_id}`}
          className="rounded-full border-cognac/40 text-cognac hover:bg-cognac/10 hover:text-cognac"
        >
          <Clock className="w-4 h-4 mr-1" />
          {booking.proposed_extra_hours > 0 ? `Logged: +${booking.proposed_extra_hours}h` : "Log extra hours"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`propose-hours-dialog-${booking.booking_id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Log extra hours worked</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-charcoal/70 text-sm space-y-3">
              <p>
                Enter the extra hours you worked beyond the original estimate. The student will see this
                pre-filled on their &ldquo;Mark done&rdquo; screen and can approve or adjust before confirming.
              </p>
              <div className="rounded-lg border border-[#1F2A40] bg-[#0B1220] p-3 space-y-2">
                <Label htmlFor={`prop-${booking.booking_id}`} className="text-charcoal text-sm">
                  Extra hours worked
                </Label>
                <Input
                  id={`prop-${booking.booking_id}`}
                  data-testid={`propose-hours-input-${booking.booking_id}`}
                  type="number"
                  min="0"
                  max="12"
                  step="0.25"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="rounded-lg h-10"
                />
                <p className="text-[11px] text-charcoal/50 leading-snug">
                  Billed at your hourly rate on completion. You keep 75%. Set to 0 to clear.
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`propose-hours-back-${booking.booking_id}`}>Never mind</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); save(); }}
            disabled={submitting}
            data-testid={`propose-hours-save-${booking.booking_id}`}
            className="bg-terracotta hover:bg-terracotta/90 text-white disabled:opacity-50"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : "Save"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
